import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/api';
import SeasonSelector from '../components/SeasonSelector';
import { gmDisplayName, gmInitial } from '../utils/displayName';

const TeamOwners = () => {
  const { t } = useTranslation();
  const [owners, setOwners] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load seasons once, default to the active season (or newest).
  useEffect(() => {
    fetchSeasons();
  }, []);

  // Reload GM balances whenever the selected season changes.
  useEffect(() => {
    if (selectedSeasonId !== '') fetchOwners(selectedSeasonId);
  }, [selectedSeasonId]);

  const fetchSeasons = async () => {
    try {
      const res = await api.get('/seasons');
      const sorted = [...(res.data || [])].sort((a, b) => b.id - a.id);
      setSeasons(sorted);
      const active = sorted.find(s => s.isActive) || sorted[0];
      if (active) {
        setSelectedSeasonId(String(active.id));
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading seasons:', err);
      setError(t('teamOwners.failedToLoad'));
      setLoading(false);
    }
  };

  const fetchOwners = async (seasonId) => {
    try {
      setLoading(true);
      const response = await api.get(`/team-owners?seasonId=${seasonId}`);
      setOwners(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching team owners:', err);
      setError(t('teamOwners.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSeasonChange = (id) => setSelectedSeasonId(id);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getWinPercentage = (wins, losses) => {
    const total = wins + losses;
    if (total === 0) return '0.0';
    return ((wins / total) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t('teamOwners.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-2">{t('teamOwners.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('teamOwners.subtitle')}
          </p>
        </div>
        <SeasonSelector seasons={seasons} selectedId={selectedSeasonId} onChange={handleSeasonChange} />
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg shadow-lg dark:shadow-gray-900 p-6">
          <div className="text-3xl font-bold">{owners.length}</div>
          <div className="text-blue-100">{t('teamOwners.totalTeamOwners')}</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg shadow-lg dark:shadow-gray-900 p-6">
          <div className="text-3xl font-bold">
            {owners.filter(o => o.twitchProfile).length}
          </div>
          <div className="text-purple-100">{t('teamOwners.streamersOnTwitch')}</div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg shadow-lg dark:shadow-gray-900 p-6">
          <div className="text-3xl font-bold">
            {owners.reduce((sum, o) => sum + o.wins + o.losses, 0)}
          </div>
          <div className="text-green-100">{t('teamOwners.totalGamesPlayed')}</div>
        </div>
      </div>

      {/* Owners Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {owners.map(owner => (
          <div 
            key={owner.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 overflow-hidden hover:shadow-xl transition-shadow"
          >
            {/* Header with Team Colors */}
            <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-700"></div>
            
            <div className="p-6">
              {/* Avatar & Name */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                  {gmInitial(owner, { preferFirstName: true })}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">
                    {gmDisplayName(owner, { preferFirstName: true })}
                  </h3>
                </div>
              </div>

              {/* Team */}
              {owner.team && (
                <Link 
                  to={`/teams/${owner.team.id}`}
                  className="block mb-4"
                >
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 hover:bg-blue-100 dark:bg-blue-900/30 transition">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-blue-600 dark:text-blue-400 font-semibold">{t('teamOwners.team')}</div>
                        <div className="font-bold text-gray-800 dark:text-gray-200">{owner.team.name}</div>
                      </div>
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {owner.team.abbreviation}
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {/* Gamertag */}
              {owner.gamertag && (
                <div className="mb-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-semibold mb-1">{t('teamOwners.gamertag')}</div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">🎮 {owner.gamertag}</div>
                </div>
              )}

              {/* Twitch */}
              {owner.twitchProfile && (
                <a
                  href={`https://twitch.tv/${owner.twitchProfile}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mb-3 bg-purple-50 border border-purple-200 rounded-lg p-3 hover:bg-purple-100 dark:bg-purple-900/30 transition"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-purple-600 text-xl">📺</span>
                    <div className="flex-1">
                      <div className="text-xs text-purple-600 font-semibold">{t('teamOwners.watchOnTwitch')}</div>
                      <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        twitch.tv/{owner.twitchProfile}
                      </div>
                    </div>
                    <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                </a>
              )}

              {/* Stats */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 font-semibold mb-1">{t('teamOwners.record')}</div>
                    <div className="text-lg font-bold text-gray-800 dark:text-gray-200">
                      {owner.wins}-{owner.losses}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 font-semibold mb-1">{t('teamOwners.winPercentage')}</div>
                    <div className="text-lg font-bold text-gray-800 dark:text-gray-200">
                      {getWinPercentage(owner.wins, owner.losses)}%
                    </div>
                  </div>
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex justify-between mb-1">
                    <span>{t('teamOwners.joined')}:</span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">{formatDate(owner.createdAt)}</span>
                  </div>
                  {owner.teamAssignedAt && (
                    <div className="flex justify-between">
                      <span>{t('teamOwners.teamAssigned')}:</span>
                      <span className="font-medium text-gray-700 dark:text-gray-300">{formatDate(owner.teamAssignedAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {owners.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-5xl mb-4">👥</div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">{t('teamOwners.noOwnersFound')}</p>
        </div>
      )}
    </div>
  );
};

export default TeamOwners;
